import React, { useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Modal from "Components/Common/Modal";
import Tooltip from "Components/Common/Tooltip";
import { toast } from "react-toastify";
import { t } from "i18next";
import pdfImg from "assets/images/pdf.png";
import getFileExtension from "utils/getFileExtension";
import { DocumentName } from "pages/UtilityConsumers/style";
import { UploadImage } from "./style";

const DEFAULT_FORMATS = ["pdf", "doc", "docx", "xls", "xlsx"];
const MAX_SIZE = 10 * 1024 * 1024;

function CommonUploadDocumentModal({
  isOpen = true,
  title = t("Upload Document"),
  description = t(
    "Uploading document is optional. You may attach a file, or simply click Submit."
  ),
  validFormats = DEFAULT_FORMATS,
  maxSize = MAX_SIZE,
  submitText = t("Submit"),
  loadingText = t("Submitting..."),
  onSubmit,
  onClose,
  handleClose,
  isSubmitting,
}) {
  const ref = useRef();
  const [selectedFile, setSelectedFile] = useState(null);

  const fileChangeHandler = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const extension = getFileExtension(file.name.toLowerCase());

    if (!validFormats.includes(extension)) {
      toast.error(t("Invalid file format"));
      return;
    }

    if (file.size > maxSize) {
      toast.error(t("File size should be less than 10 MB"));
      return;
    }

    setSelectedFile({
      file,
      name: file.name,
      url: URL.createObjectURL(file),
    });

    ref.current.value = "";
  };

  const handleRemove = () => {
    setSelectedFile(null);
    if (ref.current) ref.current.value = "";
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(selectedFile?.file || null);
      setSelectedFile(null);
      onClose();
      handleClose();
    } catch (error) {
    } 
  };

  return (
    <Modal
      isOpen={isOpen}
      maxWidth="sm"
      title={title}
      handleClose={() => {
        setSelectedFile(null);
        onClose();
      }}
    >
      <Box className="mt-3">
        <Box className="d-flex gap-3 flex-wrap mt-2">
          {selectedFile ? (
            <div className="image-container">
              <Box
                component="img"
                className="image d-block m-auto"
                alt="document"
                src={
                  getFileExtension(selectedFile.name) === "pdf"
                    ? pdfImg
                    : selectedFile.url
                }
              />

              <i
                className="ri-close-circle-line fs-3 close-btn"
                onClick={handleRemove}
              />

              <Tooltip title={selectedFile.name}>
                <DocumentName fontSize={13} variant="body2">
                  {selectedFile.name}
                </DocumentName>
              </Tooltip>
            </div>
          ) : (
            <Box
              component="label"
              className="mb-3 p-1 cursor-pointer"
              sx={{ border: "1px dashed black" }}
            >
              <UploadImage>
                <AddIcon />
              </UploadImage>

              <input
                ref={ref}
                type="file"
                hidden
                accept={validFormats.map((f) => `.${f}`).join(",")}
                onChange={fileChangeHandler}
              />
            </Box>
          )}
        </Box>

        <Typography variant="body2" sx={{ mt: 1, color: "gray" }}>
          {description}
        </Typography>

        <Box className="mt-4 d-flex justify-content-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? loadingText : submitText}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default CommonUploadDocumentModal;
